/* eslint-disable @next/next/no-img-element */
import {
  Box,
  Checkbox,
  CircularProgress,
  MenuItem,
  Rating,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import React, { useCallback } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { Product } from "@/types/General";
import { useAddWishlistMutation, useDeleteWishlistMutation } from "@/service/wishlist";
import { useAddToCartMutation } from "@/service/cart";
import { useAppDispatch, useAppSelector } from "@/lib/hook";
import {
  addToWishlistState,
  removeFromWishlistState,
  getWishlistProductIds,
} from "@/lib/slices/wishlistSlice";
import toast from "react-hot-toast";
import { getToken } from "@/lib/slices/authSlice";
import { getFromStorage } from "@/constants/storage";
import { STORAGE_KEYS } from "@/constants/storageKeys";
import { getDeliveryChannel } from "@/utils/deliveryMode";

interface ProductCardProps {
  product: {
    image: string;
    category: string;
    title: string;
    price: number;
    oldPrice: number;
    discount: number;
    rating: number;
    reviews: number;
    bestseller?: boolean;
    time?: string;
  };
}

function ProductCard({ product }: { product: Product }) {
  console.log("product imagess--", product);

  const router = useRouter();
  const searchParams = useSearchParams();
  const dispatch = useAppDispatch();
  const [color, setColor] = React.useState("10");
  const [addWishlist, { isLoading: isAdding }] = useAddWishlistMutation();
  const [deleteWishlist, { isLoading: isDeleting }] = useDeleteWishlistMutation();
  const [addToCart, { isLoading: isAddingToCart }] = useAddToCartMutation();
  
  // Local loading state for immediate feedback
  const [localWishlistLoading, setLocalWishlistLoading] = React.useState(false);

  const isWishlistLoading = isAdding || isDeleting || localWishlistLoading;

  const wishlistProductIds = useAppSelector(getWishlistProductIds);
  const reduxToken = useAppSelector(getToken);

  // Check token from both Redux and localStorage
  const token = reduxToken || getFromStorage(STORAGE_KEYS.token);

  // Get product_id with fallback for different API response structures
  const productIdForWishlist: number = Number(product?.product_id);
  const isInWishlist: boolean =
    Boolean(product?.is_wishlist) ||
    (productIdForWishlist > 0 && wishlistProductIds.includes(productIdForWishlist));

  const handleChange = (event: SelectChangeEvent) => {
    setColor(event.target.value as string);
  };

  const handleWishlistToggle = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      e.stopPropagation();

      // Prevent multiple clicks during loading
      if (isWishlistLoading) {
        return;
      }

      // Re-check token at the time of click
      const currentToken = reduxToken || getFromStorage(STORAGE_KEYS.token);

      if (!currentToken) {
        toast.error("Please login to add items to wishlist");
        router.push("/auth/login");
        return;
      }

      const productId = product?.product_id;
      const storeId = product?.store_id;

      if (!productId || !storeId) {
        toast.error("Product information is missing");
        return;
      }

      const newWishlistState = !isInWishlist;

      // Set local loading immediately for instant feedback
      setLocalWishlistLoading(true);

      // Optimistic update - happens immediately
      if (newWishlistState) {
        dispatch(addToWishlistState(Number(productId)));
      } else {
        dispatch(removeFromWishlistState(Number(productId)));
      }

      try {
        if (newWishlistState) {
          await addWishlist({
            product_id: productId,
            store_id: storeId,
            is_wishlist: true,
            type: "normal",
          }).unwrap();
          toast.success("Added to wishlist");
        } else {
          await deleteWishlist({
            product_id: productId,
            store_id: storeId,
            type: "normal",
          }).unwrap();
          toast.success("Removed from wishlist");
        }
      } catch (error: any) {
        // Revert optimistic update on error
        if (newWishlistState) {
          dispatch(removeFromWishlistState(Number(productId)));
        } else {
          dispatch(addToWishlistState(Number(productId)));
        }
        // Don't show generic error if it's a 500 error that will redirect
        if (error?.status !== 500 && error?.originalStatus !== 500) {
          toast.error(error?.data?.message || "Failed to update wishlist");
        }
      } finally {
        // Clear local loading state
        setLocalWishlistLoading(false);
      }
    },
    [addWishlist, deleteWishlist, dispatch, isInWishlist, product, router, reduxToken, isWishlistLoading]
  );

  const handleAddToCart = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    const currentToken = reduxToken || getFromStorage(STORAGE_KEYS.token);

    if (!currentToken) {
      toast.error("Please login to add items to cart");
      router.push("/auth/login");
      return;
    }

    // Validate product_id and store_id
    const productId = product?.product_id;
    const storeId = product?.store_id;

    if (!productId) {
      toast.error("Invalid product: Product ID is missing");
      return;
    }

    if (!storeId) {
      toast.error("Invalid product: Store ID is missing");
      return;
    }

    try {
      const deliveryChannel = getDeliveryChannel(searchParams);
      const payload = {
        product_id: productId,
        store_id: storeId,
        qty: 1,
        channel: deliveryChannel,
      };

      const res = await addToCart(payload).unwrap();
      if (res?.statusCode === 200) {
        toast.success("Added to cart successfully");
      } else {
        toast.error(res?.message || "Failed to add to cart");
      }
    } catch (error: any) {
      console.error("Add to cart error:", error);
      // Don't show generic error if it's a 500 error that will redirect
      if (error?.status !== 500 && error?.originalStatus !== 500) {
        toast.error(error?.data?.message || "Failed to add to cart");
      }
    }
  };

  // Get product_id and store_id with fallbacks for different API response structures
  const productId = product?.product_id;
  const storeId = product?.store_id;

  const handleProductClick = () => {
    if (!productId || !storeId) {
      toast.error("Product information is incomplete. Cannot view details.");
      return;
    }
    router.push(`/product/detail/?store_id=${storeId}&product_id=${productId}`);
  };

  return (
    <div className="productCard_item">
      <Box
        component="a"
        className="overlay_click"
        onClick={handleProductClick}
      ></Box>
      <figure>
        <img
          src={product?.image || "/images/product_default.png"}
          alt="Product"
        />

        <Checkbox
          checked={isInWishlist}
          onChange={handleWishlistToggle}
          onClick={(e) => e.stopPropagation()}
          disabled={isWishlistLoading}
          sx={{
            padding: 0,
            opacity: isWishlistLoading ? 0.7 : 1,
            cursor: isWishlistLoading ? "not-allowed" : "pointer",
            transition: "opacity 0.2s ease",
          }}
          icon={<img src="/images/heart_outline_icon.svg" alt="unchecked" />}
          checkedIcon={
            <img src="/images/heart_filled_icon.svg" alt="checked" />
          }
        />

        {/* {product.bestseller && <span className="bestseller">Best Seller</span>} */}

        <Box component="a" className="add" onClick={handleAddToCart} sx={{ cursor: "pointer" }}>
          {isAddingToCart ? <CircularProgress size={20} color="inherit" /> : <><AddIcon /> Add</>}
        </Box>
      </figure>

      <div className="card_info form">
        <div className="cat_flex">
          <p className="cat">{product?.product_name ?? ""}</p>
          <Select
            labelId="demo-simple-select-label"
            id="demo-simple-select"
            value={color}
            onChange={handleChange}
          >
            <MenuItem value={10}>White</MenuItem>
            <MenuItem value={20}>Yellow</MenuItem>
            <MenuItem value={30}>Red</MenuItem>
            <MenuItem value={40}>Blue</MenuItem>
          </Select>
        </div>

        <h3>{product?.category?.name}</h3>

        <p className="price">
          <ins>₹{product?.price?.store_price}</ins>
          <del>₹{product?.price?.mrp}</del>
          <span>- {product?.price?.percentage_off}%</span>
        </p>

        <p className="review">
          {product?.average_rating || "0.0"}
          <Rating name="read-only" value={1} max={1} readOnly />
          <span>({product?.review_count} Reviews)</span>
        </p>


        <p className="deal">
          <strong>Best Deal</strong>
          <span>₹400 with Plus</span>
        </p>

        {product?.delivery_time && (
          <p className="time">
            <img src="/images/time_icon.svg" alt="icon" />{" "}
            {product.delivery_time}
          </p>
        )}
      </div>
    </div >
  );
}

export default ProductCard;

